/**
 * Automations Page
 *
 * Main page for managing automations. Displays a list of all automations
 * and allows creation, editing, cloning, and deletion.
 */

import { faFloppyDisk, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Card } from 'src/components/Card';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Loader } from 'src/components/Loader';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { Reminder } from 'src/models/reminder/_types';
import {
  createReminder,
  deleteReminder,
  fetchReminders,
  updateReminder,
} from 'src/models/reminder/reminder';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { AutomationCard } from 'src/shared/Automation/AutomationCard';
import {
  AUTOMATION_ACTIONS,
  getAutomationAction,
} from 'src/shared/Automation/automationActions';
import { AutomationItem, FilterGroup } from 'src/shared/Automation/types';
import { Filters, SelectFilter } from 'src/shared/Filters/Filters';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { Meta } from '../../components/Meta';

import { CreateAutomationWizard } from './CreateAutomationWizard';

interface AutomationsFormValues {
  automations: AutomationItem[];
}

// Default values for a new automation
const DEFAULT_AUTOMATION: Partial<AutomationItem> = {
  reminderType: '',
  delayInterval: 0,
  delayType: 'day',
  media: ['email'],
  filters: { condition: 'AND', filters: [] },
  content: [],
  tags: '',
  count: 1,
  trainerId: 0,
  sendToIds: [],
  reportFilters: [],
};

// Per-type overrides matching v2/app/controllers/dashboard/automations.js
const AUTOMATION_TYPE_DEFAULTS: Record<string, Partial<AutomationItem>> = {
  custom: {
    delayInterval: 1,
    delayType: 'day',
    media: ['email'],
    reminderType: 'workout',
  },
  'close-out-visit': {
    content: [],
    delayInterval: 1,
    delayType: 'day',
  },
  'workout-today': {
    delayInterval: 8,
    delayType: 'hour',
    media: ['email'],
  },
  'client-tag': {
    reminderType: 'assessment',
    delayInterval: 0,
    delayType: 'hour',
  },
  message: {
    reminderType: 'client-signup',
    media: ['message'],
    delayType: 'day',
    delayInterval: 0,
  },
  'upcoming-appointment': {
    reminderType: 'upcoming-appointment',
    delayType: 'hour',
    delayInterval: 48,
  },
  'visits-left': {
    reminderType: 'visits-left',
    count: 1,
    media: ['email'],
  },
  'cancel-user': {
    delayInterval: 0,
    delayType: 'day',
    reminderType: 'cancel_subscription',
  },
  'clear-modal-alert': {
    delayInterval: 0,
    delayType: 'day',
    reminderType: 'successful-payment',
  },
};

// Build automation type filter options from AUTOMATION_ACTIONS
const AUTOMATION_TYPE_OPTIONS = Object.entries(AUTOMATION_ACTIONS)
  .map(([key, config]) => ({
    value: key,
    label: config.label,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Inner form component to access Formik context for clone functionality
 */
function AutomationsFormContent({
  onRemove,
  resetKey,
}: {
  onRemove: (index: number, automation: AutomationItem) => Promise<void>;
  resetKey: number;
}) {
  const { t } = useTranslation();
  const { values, setFieldValue } =
    useFormikContext<AutomationsFormValues>();

  const handleClone = (index: number, automation: AutomationItem) => {
    const cloned = {
      ...automation,
      id: undefined,
      name: `${automation.name || t('automations.common.automation')} (${t('automations.page.copy')})`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    } as AutomationItem;
    setFieldValue('automations', [cloned, ...values.automations]);
  };

  if (values.automations.length === 0) {
    return (
      <Card>
        <Box p={3} textAlign="center">
          <Typography variant="body1" color="text.secondary">
            {t('automations.page.noAutomationsFound')}
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      {values.automations.map((automation, index) => {
        const actionConfig = getAutomationAction(automation.customType);
        if (!actionConfig) {
          return (
            <Card key={automation.id || index}>
              <Box p={2}>
                <Typography>
                  {t('automations.page.unknownActionType', { type: automation.customType })}
                </Typography>
              </Box>
            </Card>
          );
        }

        const {
          WhatSection,
          WhatSummary,
          WhenSection,
          WhenSummary,
          showMedia,
          showFilters,
          showMessage,
          showSendTo,
          sendToPlaceholder,
        } = actionConfig;

        return (
          <AutomationCard
            key={automation.id || index}
            automation={automation}
            index={index}
            onRemove={() => {
              if (automation.id) {
                onRemove(index, automation);
              } else {
                const updated = values.automations.filter((_, i) => i !== index);
                setFieldValue('automations', updated);
              }
            }}
            onClone={() => handleClone(index, automation)}
            showMediaSection={showMedia}
            showFiltersSection={showFilters}
            showMessageSection={showMessage}
            showSendToSection={showSendTo}
            sendToPlaceholder={sendToPlaceholder}
            resetKey={resetKey}
            renderWhatSection={() => (
              <WhatSection automation={automation} index={index} />
            )}
            renderWhatSummary={() => <WhatSummary automation={automation} />}
            renderWhenSection={
              WhenSection
                ? () => <WhenSection automation={automation} index={index} />
                : undefined
            }
            renderWhenSummaryOverride={
              WhenSummary
                ? () => <WhenSummary automation={automation} />
                : undefined
            }
          />
        );
      })}
    </Stack>
  );
}

/**
 * Main Automations page - displays list of automations and allows creation/editing
 */
export default function AutomationsPage() {
  const { t } = useTranslation();
  const { session } = useSession();
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteConfirmProps, confirm] = useConfirmDialogComponent();
  const formSubmitRef = useRef<(() => void) | null>(null);
  // Key to reset all card sections when Save All is clicked
  const [resetKey, setResetKey] = useState(0);

  // URL-based state for filtering
  const {
    params,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams({
    defaultPerPage: 100,
  });

  // Get filter value
  const customTypeFilter = getFilterParamValue('customType') as string | undefined;

  const { data: reminders = [], isLoading } = useQuery({
    queryKey: ['automations', customTypeFilter],
    queryFn: () =>
      fetchReminders({
        fetchAll: true,
        customType: customTypeFilter || undefined,
      }),
  });

  const createMutation = useMutation({
    mutationFn: (automation: Reminder) => createReminder(automation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      setWizardOpen(false);
      toast.success(t('automations.page.createdSuccessfully'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('automations.page.failedToCreate'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (automation: Partial<Reminder>) => updateReminder(automation),
    onSuccess: (automation) => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      toast.success(automation.name
        ? t('automations.page.updatedSuccessfullyNamed', { name: automation.name })
        : t('automations.page.updatedSuccessfully'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('automations.page.failedToUpdate'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteReminder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      toast.success(t('automations.page.deletedSuccessfully'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('automations.page.failedToDelete'));
    },
  });

  // Convert Reminder[] to AutomationItem[]
  const normalizeAutomations = (data: Reminder[]): AutomationItem[] => {
    return data.map((r) => ({
      ...r,
      content: Array.isArray(r.content) ? r.content : (r.content ? [r.content] : []),
      filters: (r.filters as unknown as FilterGroup) || {
        condition: 'AND',
        filters: [],
      },
    }));
  };

  const initialValues: AutomationsFormValues = {
    automations: normalizeAutomations(reminders),
  };

  const handleSubmit = async (
    values: AutomationsFormValues,
    helpers: FormikHelpers<AutomationsFormValues>
  ) => {
    const updatePromises = values.automations.map((automation) => {
      // Cast to Reminder type for API compatibility
      const reminderData = automation as unknown as Reminder;
      if (automation.id) {
        return updateMutation.mutateAsync(reminderData);
      } else {
        return createMutation.mutateAsync(reminderData);
      }
    });

    try {
      await Promise.all(updatePromises);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const handleRemove = async (index: number, automation: AutomationItem) => {
    if (automation.id) {
      const confirmed = await confirm({
        title: t('automations.page.deleteTitle'),
        message: t('automations.page.deleteConfirm'),
      });
      if (confirmed) {
        deleteMutation.mutate(automation.id);
      }
    }
  };

  // Filter config for the Filters component
  const filterConfigs = [
    {
      field: 'customType',
      label: t('automations.page.automationType'),
      Component: SelectFilter,
      props: {
        options: AUTOMATION_TYPE_OPTIONS,
      },
    },
  ];

  return (
    <>
      <Meta title={t('automations.page.title')} brand={session?.brand} />
      <AppFrameConfig
        id="automations"
        title={t('automations.page.title')}
        containerMaxWidth="lg"
        fixedHeader
        actions={[
          {
            label: t('automations.page.saveAll'),
            icon: faFloppyDisk,
            variant: 'bordered',
            onClick: () => {
              setResetKey((k) => k + 1);
              formSubmitRef.current?.();
            },
          },
          {
            label: t('automations.page.createNew'),
            icon: faPlus,
            variant: 'prominent',
            onClick: () => setWizardOpen(true),
          },
        ]}
      />

      <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
        <Filters
          filters={filterConfigs}
          hideMainDropdown
          params={params}
          updateParam={updateParam}
          updateFilterParam={updateFilterParam}
          getFilterParamValue={getFilterParamValue}
          resetFilterParams={resetFilterParams}
        />
      </SectionToolbar>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <Loader size="large" />
        </Box>
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ handleSubmit: formikSubmit, values, setFieldValue }) => {
            // Store submit function in ref for AppFrameConfig action
            formSubmitRef.current = () => formikSubmit();

            const handleWizardAdd = (data: {
              customType: string;
              reminderType: string;
              name: string | null;
            }) => {
              const typeDefaults = AUTOMATION_TYPE_DEFAULTS[data.customType] || {};
              const automation = {
                ...DEFAULT_AUTOMATION,
                ...typeDefaults,
                customType: data.customType,
                reminderType: data.reminderType || typeDefaults.reminderType || 'all',
                name: data.name,
                trainerId: session?.trainerId || 0,
                createdAt: Date.now(),
                updatedAt: Date.now(),
              } as AutomationItem;
              setFieldValue('automations', [automation, ...values.automations]);
              setWizardOpen(false);
            };

            return (
              <>
                <form onSubmit={formikSubmit}>
                  <AutomationsFormContent
                    onRemove={handleRemove}
                    resetKey={resetKey}
                  />
                </form>
                <CreateAutomationWizard
                  open={wizardOpen}
                  onClose={() => setWizardOpen(false)}
                  onAdd={handleWizardAdd}
                />
              </>
            );
          }}
        </Formik>
      )}

      <ConfirmDialog {...deleteConfirmProps} />
    </>
  );
}
